/** Composition principale : générique, étapes (voix + scène), fin ; barre d'avancement et sous-titres. */
import { AbsoluteFill, Audio, Sequence, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import { StepScene } from "./scene/StepScene.js";
import { buildTimeline, captionsOf, FPS, sceneStateAt, stepAt, type Rhythm, type SpokenNarration, type VideoLesson } from "@jange/shared";
import { THEMES, type Format, type StyleId } from "./styles.js";

export type LessonVideoProps = {
  /** `kind: "solution"` : corrigé d'une question d'annales (titre « BFEM Session 2024 · Ex. 2 »). */
  lesson: VideoLesson & { title: string; kind?: "content" | "solution" };
  /** Narration synthétisée : chaque temps porte l'audio, en URL (poste de rendu, aperçu du studio) ou en chemin du dossier public (pilote). */
  narration: SpokenNarration;
  style: StyleId;
  format: Format;
  rhythm: Rhythm;
  captions: boolean;
  /** URL des fichiers cités par la leçon (modèles 3D), par identifiant. */
  assetUrls?: Record<string, string>;
  subject: string;
  grade: string;
};

const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

const FORMULA_CSS = (scale: number) => `
.jg-formula .katex { font-size: ${scale}em; }
.jg-formula .katex-display { margin: 0; }
.jg-formula .katex .text { font-family: inherit; }
`;

export function LessonVideo({ lesson, narration, style, format, rhythm, captions, subject, grade, assetUrls = {} }: LessonVideoProps) {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const theme = THEMES[style];
  const lang = narration.lang;
  const tl = buildTimeline(lesson, narration, lang, rhythm);
  const step = stepAt(tl, frame);
  const state = sceneStateAt(lesson, tl, frame);
  const caps = captions ? captionsOf(tl, rhythm) : [];
  const cap = caps.find((c) => frame >= c.from && frame < c.to);
  const portrait = format === "9:16";
  const inOutro = frame >= tl.outro.from;
  const kIntro = easeOut(clamp01(frame / (0.6 * FPS)));
  const kOutro = easeOut(clamp01((frame - tl.outro.from) / (0.6 * FPS)));
  const stepIndex = step ? step.index : inOutro ? lesson.steps.length - 1 : -1;

  return (
    <AbsoluteFill style={{ background: theme.bg, backgroundImage: theme.bgImage, color: theme.text, fontFamily: theme.fontFamily }}>
      <style>{FORMULA_CSS(theme.formulaScale)}</style>
      <svg width={0} height={0} style={{ position: "absolute" }} aria-hidden>
        <filter id="chalk" x="-2%" y="-2%" width="104%" height="104%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="2.4" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </svg>
      {theme.rough && <AbsoluteFill style={{ opacity: 0.18, backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0'/></filter><rect width='400' height='400' filter='url(%23n)'/></svg>\")" }} />}

      {/* Voix : un fichier par temps, calé sur la chronologie. */}
      {tl.steps.flatMap((s) => s.beats.filter((b) => b.audio).map((b, i) => (
        <Sequence key={`${s.id}-${i}`} from={b.from} durationInFrames={b.frames}><Audio src={/^(https?:|blob:|data:)/.test(b.audio!) ? b.audio! : staticFile(b.audio!)} /></Sequence>
      )))}

      {/* Avancement : un segment par étape, comme dans l'app. */}
      {!inOutro && frame >= tl.intro.frames && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, display: "flex", gap: 6, padding: `${portrait ? 24 : 20}px ${portrait ? 56 : 72}px 0` }}>
          {lesson.steps.map((s, i) => <div key={s.id} style={{ height: 8, flex: 1, borderRadius: 999, background: i <= stepIndex ? (theme.rough ? theme.amber : theme.accent) : theme.rough ? "rgba(243,241,232,0.25)" : theme.cardBorder }} />)}
        </div>
      )}

      {frame < tl.intro.frames && (
        <AbsoluteFill style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: portrait ? 80 : 120, textAlign: "center", opacity: kIntro }}>
          <div style={{ transform: `translateY(${(1 - kIntro) * 24}px)`, display: "flex", flexDirection: "column", gap: 28, alignItems: "center" }}>
            <div style={{ fontSize: portrait ? 30 : 32, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.rough ? theme.amber : theme.accent }}>{lesson.kind === "solution" ? "Corrigé · " : ""}{subject} · {grade}</div>
            <h1 style={{ margin: 0, fontSize: portrait ? 84 : 96, lineHeight: 1.08, fontWeight: 800, maxWidth: 1400 }}>{lesson.title}</h1>
            <div style={{ width: 160 * easeOut(clamp01((frame - 10) / 20)), height: 6, borderRadius: 999, background: theme.rough ? theme.amber : theme.accent }} />
            <div style={{ fontSize: portrait ? 34 : 34, color: theme.muted }}>{lesson.steps.length} étapes · {Math.round(tl.total / FPS / 60)} min</div>
          </div>
        </AbsoluteFill>
      )}

      {step && <StepScene lesson={lesson} step={step} state={state} frame={frame} theme={theme} format={format} width={width} height={height} lang={lang} assetUrls={assetUrls} />}

      {inOutro && (
        <AbsoluteFill style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: portrait ? 80 : 120, textAlign: "center", opacity: kOutro }}>
          <div style={{ transform: `translateY(${(1 - kOutro) * 24}px)`, display: "flex", flexDirection: "column", gap: 24, alignItems: "center" }}>
            <h1 style={{ margin: 0, fontSize: portrait ? 84 : 92, fontWeight: 800 }}>Bravo !</h1>
            <div style={{ fontSize: portrait ? 40 : 42, color: theme.muted, maxWidth: 1200, lineHeight: 1.35 }}>{lesson.kind === "solution"
              ? "À toi : refais cette question sans regarder le corrigé, puis entraîne-toi sur ses notions dans l'app Jang-e."
              : `Entraîne-toi maintenant dans l'app Jang-e : la leçon « ${lesson.title} » t'attend avec ses exercices.`}</div>
            <div style={{ marginTop: 20, fontSize: portrait ? 44 : 44, fontWeight: 800, color: theme.rough ? theme.amber : theme.accent }}>Jang-e</div>
          </div>
        </AbsoluteFill>
      )}

      {cap && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: portrait ? 120 : 56, display: "flex", justifyContent: "center", padding: "0 56px" }}>
          <div style={{ background: "rgba(8,12,24,0.78)", color: "#FFFFFF", fontFamily: THEMES.presentation.fontFamily, fontSize: portrait ? 36 : 34, fontWeight: 600, lineHeight: 1.3, padding: "12px 24px", borderRadius: 14, maxWidth: portrait ? 960 : 1500, textAlign: "center" }}>{cap.text}</div>
        </div>
      )}
    </AbsoluteFill>
  );
}
